import { RouteConfig } from 'vue-router';
import { getModule } from 'vuex-module-decorators';
import CoreStore from '@/core/store';
import { store } from '@/store';
import LoginLayout from '@/login/components/LoginLayout.vue';
import { resolveRoute } from '@/core/routing/resolve-route';

const coreStore = getModule(CoreStore, store);

export const LoginRoutes: RouteConfig[] = [
	{
		path: '',
		component: LoginLayout,
		beforeEnter: resolveRoute({
			pre: () => {
				coreStore.setIsPageLoaderShown(true);
				coreStore.setIsPageErrorShown(false);
			},
			errorHandler: (error: Error) => {
				coreStore.setIsPageErrorShown(true);
				console.error(error);
			},
			post: () => coreStore.setIsPageLoaderShown(false),
			resolvers: [],
		}),
		children: [
			{
				path: '/',
				name: 'login',
				component: () => import('@/login/views/Login/Login.vue'),
				meta: {
					title: 'Welcome To Kasasa',
					headline: 'Log in with your Kasasa account.',
					redirectOnAuth: true,
				},
				props: (route) => ({
					profileConnectionToken: route.query.token,
				}),
			},
			{
				path: '/sso', // done
				name: 'sso',
				component: () => import('@/login/views/Sso.vue'),
				meta: {
					title: 'Kasasa SSO',
				},
				props: (route) => ({
					code: route.query.code,
				}),
			},
			{
				path: '/login',
				redirect: '/',
			},
			{
				path: '/verify',
				name: 'mfa',
				component: () => import('@/login/views/MFA/MFA.vue'),
				meta: {
					title: 'Two-Factor Authentication',
					headline: 'Two-Factor Authentication',
					redirectOnAuth: true,
				},
				props: (route) => ({
					profileConnectionToken: route.query.token,
				}),
			},
			{
				path: '/forgot-username',
				name: 'forgotUsername',
				component: () => import('@/login/views/ForgotUsername/ForgotUsername.vue'),
				meta: {
					title: 'Forgot your username?',
					headline: 'Forgot your username?',
					redirectOnAuth: true,
				},
			},
			{
				path: '/username-reminder',
				name: 'usernameReminder',
				component: () => import('@/login/views/Static/Static.vue'),
				meta: {
					title: 'Check your email.',
					headline: 'Check your email.',
					staticData: {
						message: 'If we found a matching account, you will receive an email from us with your Kasasa username.',
						buttonLabel: 'Log In',
						buttonRoute: 'login',
					},
				},
			},
			{
				path: '/forgot-password',
				name: 'forgotPassword',
				component: () => import('@/login/views/ForgotPassword/ForgotPassword.vue'),
				meta: {
					title: 'Forgot your password?',
					headline: 'Forgot your password?',
					redirectOnAuth: true,
				},
			},
			{
				path: '/forgot-password-email-sent',
				name: 'forgotPasswordEmailSent',
				component: () => import('@/login/views/Static/Static.vue'),
				meta: {
					title: 'Forgot your password?',
					headline: 'Forgot your password?',
					staticData: {
						message: 'Check your mobile device. If your username was found, we texted you a code you can use to reset your password.',
						buttonLabel: 'Reset Password',
						buttonRoute: 'resetPassword',
					},
				},
			},
			{
				path: '/password-changed',
				name: 'passwordChanged',
				component: () => import('@/login/views/Static/Static.vue'),
				meta: {
					title: 'Password Changed',
					headline: 'Your password was successfully changed.',
					staticData: {
						message: 'You can now login with your username and your new password.',
						buttonLabel: 'Log In',
						buttonRoute: 'login',
					},
				},
			},
			{
				path: '/reset-password',
				name: 'resetPassword',
				component: () => import('@/login/views/ResetPassword/ResetPassword.vue'),
				meta: {
					title: 'Reset Password',
					headline: 'Reset Password',
				},
			},
			{
				path: '/password-reset',
				name: 'passwordReset',
				component: () => import('@/login/views/Static/Static.vue'),
				meta: {
					title: 'Password reset.',
					headline: 'Password reset.',
					staticData: {
						message: 'Your password has been updated.',
						buttonLabel: 'Log In',
						buttonRoute: 'login',
					},
				},
			},
			{
				path: '/account-locked',
				name: 'accountLocked',
				component: () => import('@/login/views/Static/Static.vue'),
				meta: {
					title: 'Account Locked',
					headline: 'Your account has been temporarily locked.',
					staticData: {
						message: 'Please contact your Kasasa institution for assistance.',
						buttonLabel: '',
						buttonRoute: '',
					},
				},
			},
			{
				path: '/logout',
				name: 'logout',
				component: () => import('@/login/views/Static/Static.vue'),
				meta: {
					title: 'You have been logged out.',
					headline: 'You have been logged out.',
					staticData: {
						message: '',
						buttonLabel: 'Log In',
						buttonRoute: 'login',
					},
				},
			},
			{
				path: '/session-expired',
				name: 'sessionExpired',
				component: () => import('@/login/views/Static/Static.vue'),
				meta: {
					title: 'Your session has expired.',
					headline: 'Your session has expired.',
					staticData: {
						message: '',
						buttonLabel: 'Log In',
						buttonRoute: 'login',
					},
				},
			},
			{
				path: '/verify-email-change/:token',
				name: 'emailChangeVerification',
				component: () => import('@/login/views/EmailChangeVerification/EmailChangeVerification.vue'),
				meta: {
					title: 'Verify Email Address Change',
					headline: 'Verify Email Address Change',
					validateToken: true,
					tokenFailureRoute: 'emailChangeTokenExpiredMessage',
				},
				props: (route) => ({
					token: route?.params?.token,
					failureRoute: route?.meta?.tokenFailureRoute,
				}),
			},
			{
				path: '/email-change-link-expired',
				name: 'emailChangeTokenExpiredMessage',
				component: () => import('@/login/views/Static/Static.vue'),
				meta: {
					title: 'This link has expired.',
					headline: 'This link has expired.',
					staticData: {
						message: 'Have you already confirmed your email change? If not, you can login to restart the process.',
						buttonLabel: 'Log In',
						buttonRoute: 'login',
					},
				},
			},
			{
				path: '/loan-invite/:token',
				name: 'kasasaLoanInviteVerification',
				component: () => import('@/login/views/ProductInvite/ProductInvite.vue'),
				meta: {
					validateToken: true,
					hideWell: true,
					tokenFailureRoute: 'inviteTokenExpiredMessage',
					hideConsumerLogin: true,
				},
				props: (route) => ({
					token: route.params.token,
					inviteType: typeof route.query.type === 'string' ? parseInt(route.query.type, 10) : null,
				}),
			},
			{
				path: '/invitation-expired',
				name: 'inviteTokenExpiredMessage',
				component: () => import('@/login/views/Static/Static.vue'),
				meta: {
					title: 'Your dashboard setup link has expired.',
					headline: 'Your dashboard setup link has expired.',
					staticData: {
						message: 'Please contact your Kasasa institution for assistance.',
						buttonLabel: 'Already created a Kasasa login?',
						buttonRoute: 'login',
					},
				},
			},
			{
				path: '/create-login',
				name: 'createLogin',
				component: () => import('@/login/views/CreateLogin/CreateLogin.vue'),
				meta: {
					title: 'Create Kasasa Login',
					headline: 'First, create your Kasasa login.',
					hideWell: true,
					enrollmentStep: true,
				},
			},
			{
				path: '/verify-mobile-device',
				name: 'verifyMobileDevice',
				component: () => import('@/login/views/VerifyMobileDevice/VerifyMobileDevice.vue'),
				meta: {
					title: 'Verify Mobile Device',
					headline: 'Verify your mobile device.',
					hideWell: true,
					enrollmentStep: true,
				},
			},
			{
				path: '/pin',
				name: 'pinSetup',
				component: () => import('@/login/views/PinSetup/PinSetup.vue'),
				meta: {
					title: 'Create Support code',
					headline: 'Create a Support Code.',
					hideWell: true,
					enrollmentStep: true,
				},
			},
			{
				path: '/enrollment-complete',
				name: 'enrollmentComplete',
				component: () => import('@/login/views/EnrollmentComplete/EnrollmentComplete.vue'),
				meta: {
					title: 'Enrollment Complete',
					headline: 'Success!',
				},
			},
			{
				path: '/enrollment-session-expired',
				name: 'enrollmentSessionExpiredMessage',
				component: () => import('@/login/views/Static/Static.vue'),
				meta: {
					title: 'Your session has expired.',
					headline: 'Your session has expired.',
					staticData: {
						message: 'Please use the link included in your email<br/>from Kasasa to restart enrollment.',
						buttonLabel: '',
						buttonRoute: '',
					},
				},
			},
			{
				path: '/terms-of-use',
				name: 'termsOfUse',
				component: () => import('@/login/views/Terms/Terms.vue'),
				meta: {
					title: 'Terms Of Use',
					headline: 'Terms Of Use',
				},
			},
			{
				path: '*',
				component: () => import('@/login/views/Static/Static.vue'),
				meta: {
					title: 'Page Not Found',
					headline: 'Page Not Found',
					staticData: {
						message: "Sorry, there's no page here!",
						buttonLabel: 'Log In',
						buttonRoute: 'login',
					},
				},
			},
		],
	},
];
